import { useTranslation } from 'next-i18next';
import React, { ReactElement, useCallback } from 'react';
import { FdrProductType } from '@hotelplan/supergraph-api';
import {
  createGeoProductVariables,
  mapProductFeatureGroupsToProductFeatures,
} from 'components/domain/fdr-geo/fdr-geo.utils';
import FdrBlogRecommenderSection from 'components/domain/fdr-page-components/recommenders/blog-article/fdr-blog-recommender';
import { FdrProductRecommender } from 'components/domain/fdr-page-components/recommenders/product/fdr-product-recommender';
import { FdrThemeRecommender } from 'components/domain/fdr-page-components/recommenders/theme/fdr-theme-recommender';
import useFdrPageComponentsFetch from 'components/domain/fdr-page-components/useFdrPageComponentsFetch';
import { isFdrAdventureTravelProduct } from 'components/domain/fdr-product/fdr-product.utils';
import { FdrAllBlogArticlesResponseFragment } from 'schemas/fragment/blog-articles/fdr-all-blog-articles.generated';
import { FdrRecommendedAdventureTravelsFragment } from 'schemas/fragment/product/fdr-adventure-travel.generated';
import { FdrThemeFragment } from 'schemas/fragment/recommenders/theme/fdr-theme-recommender.generated';
import { FdrProductBlogArticlesDocument } from 'schemas/query/product/fdr-product-blog-articles.generated';
import { FdrProductRecommendedProductsDocument } from 'schemas/query/product/fdr-product-recommended-products.generated';
import {
  createProductBlogVariables,
  mapFdrProductBlogResponseToArticles,
  mapFdrProductRecommendedProducts,
} from './fdr-pdp.utils';

const PDP_THEMES_FONT_COLOR = '#ffffff';
const PDP_THEME_BG_COLOR = '#7A2739';

interface IFdrPdpRecommendationsProps {
  id: string;
  themes: Array<FdrThemeFragment>;
  blogArticles: FdrAllBlogArticlesResponseFragment;
  products: FdrRecommendedAdventureTravelsFragment;
  countryName?: string;
}

export function FdrPdpRecommendations({
  id,
  themes,
  products,
  blogArticles,
  countryName = '',
}: IFdrPdpRecommendationsProps): ReactElement {
  const [t] = useTranslation(['pdp', 'geo']);

  const mapGeoProductVariables = useCallback(
    (page, perPage) => {
      return createGeoProductVariables(id, page, perPage, [
        FdrProductType.AdventureTravel,
      ]);
    },
    [id]
  );

  const { fetchItems: fetchRecommendedProducts, loading } =
    useFdrPageComponentsFetch(
      FdrProductRecommendedProductsDocument,
      mapGeoProductVariables,
      mapFdrProductRecommendedProducts
    );

  const mapProductBlogVariables = useCallback(
    (page, perPage) => {
      return createProductBlogVariables(id, page, perPage);
    },
    [id]
  );

  const { fetchItems: fetchBlogArticles } = useFdrPageComponentsFetch(
    FdrProductBlogArticlesDocument,
    mapProductBlogVariables,
    mapFdrProductBlogResponseToArticles
  );

  const adventureTravels =
    products?.productsPage?.filter(isFdrAdventureTravelProduct) || [];

  return (
    <>
      {adventureTravels?.length ? (
        <FdrProductRecommender
          sectionHeaderTitle={t('common:product.recommender.section.title')}
          component={{
            name: t('geo:products.title', { geoObject: countryName }),
            items: adventureTravels.map(p => ({
              ...p,
              link: p.webMeta.descriptionWebMeta.link,
              image: p.images[0],
              features: mapProductFeatureGroupsToProductFeatures(
                p.featureGroups
              ),
              route: p.route?.map(r => r.locationName),
            })),
            total: products.pagination.total,
          }}
          fetchItems={fetchRecommendedProducts}
          loading={loading}
          testId="product-recommendations-section"
        />
      ) : null}
      {themes?.length ? (
        <FdrThemeRecommender
          total={themes.length}
          items={themes.map(theme => ({
            ...theme,
            link: theme.webMeta.descriptionWebMeta?.link,
          }))}
          title={t('geo:themes.title', { geoObject: countryName })}
          fontColor={PDP_THEMES_FONT_COLOR}
          bgColor={PDP_THEME_BG_COLOR}
        />
      ) : null}
      {blogArticles?.blogArticles?.length ? (
        <FdrBlogRecommenderSection
          component={{
            id: 'fdr-pdp-blog-recommender',
            title: t('pdp:blogArticles.title'),
            blogArticleItems: {
              blogArticles: blogArticles.blogArticles,
              pagination: blogArticles.pagination,
            },
          }}
          fetchItems={fetchBlogArticles}
        />
      ) : null}
    </>
  );
}
